import { useToast } from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toastGenerator } from "../index";

/**
 * A custom hook for clipboard operations with toast notifications
 * @returns {{ copied: boolean, copyToClipboard: (text: string) => Promise<void>, resetCopied: () => void }}
 */
function useClipboard({ timeout = 2000 } = {}) {
  const toast = useToast();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [lastCopiedText, setLastCopiedText] = useState("");

  // Check clipboard API availability once on mount
  const isClipboardSupported = useMemo(() => !!navigator.clipboard && !!navigator.clipboard.writeText, []);

  const copyToClipboard = useCallback(
    async (text) => {
      // Validate input
      if (typeof text !== "string" || text.trim() === "") {
        toast(toastGenerator({ status: "error", message: t("label.invalid_copy_text") }));
        return;
      }

      if (!isClipboardSupported) {
        toast(toastGenerator({ status: "error", message: t("label.clipboard_not_supported") }));
        return;
      }

      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setLastCopiedText(text);
        toast(toastGenerator({ status: "success", message: t("label.copied_to_clipboard") }));

        // Reset copied state after timeout
        const timer = setTimeout(() => setCopied(false), timeout);
        return () => clearTimeout(timer);
      } catch (error) {
        toast(toastGenerator({ status: "error", message: t("label.copy_failed") }));
        console.error(t("label.clipboard_copy_failed"), error);
        throw error; // Allow caller to handle the error if needed
      }
    },
    [isClipboardSupported, toast, timeout, t],
  );

  // Manual reset function
  const resetCopied = useCallback(() => {
    setCopied(false);
  }, []);

  return {
    copied,
    copyToClipboard,
    resetCopied,
    lastCopiedText,
    isClipboardSupported,
  };
}

export default useClipboard;

// Usage example:
// const {
//   copied,
//   copyToClipboard,
//   resetCopied,
//   lastCopiedText,
//   isClipboardSupported
// } = useClipboard({ timeout: 3000 });
//
// const handleCopy = async () => {
//   try {
//     await copyToClipboard("Hello, world!");
//   } catch (err) {
//     Handle error
//   }
// };
// if (copied) console.log(`Last copied: ${lastCopiedText}`);
